import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';
import React from 'react';
import { Button, Container } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faPlay, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { loginRequest } from '../../authConfig';
import { LogsDataService } from '../../Services/LogsDataService';
import { SchedulerDataService } from '../../Services/SchedulerDataService';
import ServerTable from '../../Components/Table/ServerTabe';
import { ExecutionRunDetailsModal, ExecutionStatusBadge, formatDuration } from '../../Components/ExecutionRuns/ExecutionRuns';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import TaskConfigModal from '../SchedulerTaskListPage/TaskConfigModal';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import './LogsListPage.scss';

const statusFilterOptions = [
  { value: 'Started', label: 'Started' },
  { value: 'InProgress', label: 'In Progress' },
  { value: 'Succeeded', label: 'Succeeded' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Warning', label: 'Warning' }
];

export const LogsListPageContent = () => {
    const readOnly = useReadOnly(Permissions.Extractions.ScheduledTasks);
  const { instance, accounts, inProgress } = useMsal();
  const [selectedExecution, setSelectedExecution] = React.useState(null);
  const [showRunModal, setShowRunModal] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const acquireToken = React.useCallback(async () => {
    const request = { ...loginRequest, account: accounts[0] };
    return (await instance.acquireTokenSilent(request)).accessToken;
  }, [accounts, instance]);

  const fetchExecutions = React.useCallback(async ({ pageIndex, pageSize, sorting, columnFilters, globalFilter }) => {
    if (inProgress !== 'none' || !accounts.length) return { items: [], totalItems: 0 };

    const filters = Object.fromEntries((columnFilters || []).map(({ id, value }) => [id, value]));
    const sort = sorting?.[0];
    const result = await LogsDataService.getLogs(await acquireToken(), {
      category: filters.category || '',
      processType: filters.processType || '',
      status: filters.status || '',
      search: globalFilter || filters.message || '',
      scheduledJobId: filters.scheduledJobId || '',
      sortBy: sort?.id || '',
      sortDir: sort ? (sort.desc ? 'desc' : 'asc') : '',
      page: pageIndex + 1,
      pageSize
    });

    return {
      items: result?.items || [],
      totalItems: result?.total ?? result?.totalItems ?? 0
    };
  }, [accounts.length, acquireToken, inProgress]);

  const runAdHoc = async (meta) => {
    if (readOnly) return;
    try {
      setRunning(true);
      await SchedulerDataService.executeAdHocTask(meta, await acquireToken());
      setShowRunModal(false);
      setRefreshKey((value) => value + 1);
    } catch (error) {
      console.error('Failed to execute task:', error);
    } finally {
      setRunning(false);
    }
  };

  const columns = React.useMemo(() => [
    { header: 'Process type', accessorKey: 'processType' },
    { header: 'Category', accessorKey: 'category' },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (info) => <ExecutionStatusBadge status={info.getValue()} />,
      meta: { filterVariant: 'select', filterOptions: statusFilterOptions }
    },
    {
      header: 'Started',
      accessorKey: 'startedAt',
      cell: (info) => info.getValue() ? formatGuatemalanDateTime(info.getValue()) : '-',
      enableColumnFilter: false
    },
    {
      header: 'Duration',
      accessorKey: 'durationSeconds',
      cell: (info) => formatDuration(info.getValue()),
      enableColumnFilter: false
    },
    { header: 'Message', accessorKey: 'message' },
    { header: 'Scheduled job', accessorKey: 'scheduledJobId' },
    {
      header: '',
      id: 'details',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <Button size="sm" variant="outline-secondary" onClick={() => setSelectedExecution(row.original)}>
          <FontAwesomeIcon icon={faMagnifyingGlass} className="me-1" /> Details
        </Button>
      )
    }
  ], []);

  return (
    <Container fluid className="execution-logs-page h-100 d-flex flex-column">
      <div className="d-flex justify-content-end align-items-center mb-2 gap-2">
        <Button variant="outline-primary" onClick={() => setRefreshKey((value) => value + 1)} title="Refresh executions">
          <FontAwesomeIcon icon={faRefresh} />
        </Button>
        <Button variant="primary" disabled={readOnly || (running)} onClick={() => setShowRunModal(true)}>
          <FontAwesomeIcon icon={faPlay} className="me-2" />
          {running ? 'Running...' : 'Run task'}
        </Button>
      </div>

      <div className="execution-logs-table-region">
        <ServerTable
          columns={columns}
          fetchData={fetchExecutions}
          tableClass="table-hover align-middle"
          containerClass="table-responsive"
          headerClass="table-light"
          filtersHeaderClass="table-light"
          enableGlobalFilter={false}
          initialPageSize={10}
          pageSizeOptions={[10, 25, 50, 100]}
          refreshKey={refreshKey}
          fillAvailableHeight
        />
      </div>

      {showRunModal && <TaskConfigModal show onHide={() => setShowRunModal(false)} onNext={runAdHoc} />}
      <ExecutionRunDetailsModal execution={selectedExecution} onHide={() => setSelectedExecution(null)} />
    </Container>
  );
};

const LogsListPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Execution Logs">
      <LogsListPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default LogsListPage;
